import { Spacer } from 'components/UI/Spacer';
import { Typography } from 'components/UI/Typography';
import { Svgs, Colors } from 'environment';
import {
	LicenseModelQuantities,
	SubscriptionNameLabel,
	UserLicenseLabels,
	UserLicenseModel
} from 'store/account/subscription';

import { Wrapper, LicenseInfoContainer } from './SubscriptionDetails.style';
import { Flex } from 'components/UI/Flex';
import { Button } from 'components/UI/Interactables/Button';
import {
	useTranslation,
	useSubscriptionApiKey,
	useSubscription,
	useLicenseOwnerFullName,
	useSubscriptionRules,
	useSubscriptionBundleFlag,
	useLicenseModels
} from 'hooks/store';
import UpdatingSubscriptionText from '../Common/UpdatingSubscriptionText/UpdatingSubscriptionText';
import { isCollaborator } from 'helpers/collaborators';

interface SubscriptionDetailsProps {
	setChangePlanModalVisible: (visible: boolean) => void;
}

export function SubscriptionDetails({ setChangePlanModalVisible }: SubscriptionDetailsProps) {
	const { translate } = useTranslation();
	const [{ data: subscriptionApiKey }] = useSubscriptionApiKey();

	const { subscriptionBundleFlag } = useSubscriptionBundleFlag();

	const [
		{
			data: {
				details,
				subscriptionTypes: {
					isNonSubscriber,
					isLedidiEnterprise,
					isLedidiCore,
					isLedidiCollaborator,
					isLedidiFreeTrial
				},
				userTypesAndRoles: { isSubscriptionLicenseUser, isPaidSubscriptionOwner }
			}
		}
	] = useSubscription();

	const [
		{
			data: {
				totalLicensesCount,
				usedLicensesCount,
				licenseQuantitiesByName,
				usedLicenseQuantitiesByName
			}
		}
	] = useLicenseModels();

	const licenseOwnerFullName = useLicenseOwnerFullName();

	const { canChangePlan, canCancelSubscription, isSubscriptionUpdating } = useSubscriptionRules();

	const isOnePaidLincenseOwner =
		details &&
		details.availableLicenseQuantity === 0 &&
		details.licenseQuantity === 1 &&
		isPaidSubscriptionOwner;

	function SubscriptionIcon() {
		if (isLedidiCollaborator) return <Svgs.IconCollaboratorPlan />;
		if (isLedidiCore && isOnePaidLincenseOwner) return <Svgs.AvatarPlanIcon />;
		if (isLedidiCore) return <Svgs.UsersNetwork />;
		if (isLedidiEnterprise) return <Svgs.IconEnterprisePlan />;
		return <Svgs.FreePlanIcon />;
	}

	function generateTotalLicensesText() {
		let totalLicensesString =
			totalLicensesCount +
			' ' +
			translate(({ accountUM }) =>
				totalLicensesCount > 1
					? accountUM.subscription.licensesInTotal
					: accountUM.subscription.licenseInTotal
			);
		if (subscriptionBundleFlag && licenseQuantitiesByName) {
			const licensesString = generateQuantitiesText(licenseQuantitiesByName);
			if (licensesString) totalLicensesString = totalLicensesString + ': ' + licensesString;
		}
		return totalLicensesString;
	}

	function generateActiveLicensesText() {
		let usedLicensesString =
			translate(({ accountUM }) => accountUM.subscription.activeLicenses) +
			' ' +
			usedLicensesCount;

		if (subscriptionBundleFlag && usedLicenseQuantitiesByName && !isCollaborator) {
			const licensesString = generateQuantitiesText(usedLicenseQuantitiesByName);
			if (licensesString)
				usedLicensesString = usedLicensesString + ' (' + licensesString + ')';
		}
		return usedLicensesString;
	}

	/**
	 * returns text of the counted licenses by license model
	 */
	function generateQuantitiesText(totalLicensesQuantities: LicenseModelQuantities) {
		let licensesString = '';
		// extract just the licenses quantities that are not 0
		let quantities: LicenseModelQuantities = {};
		Object.keys(totalLicensesQuantities).forEach(key => {
			if (totalLicensesQuantities[key] > 0)
				quantities = { ...quantities, [key]: totalLicensesQuantities[key] };
		});
		// add bundle licenses text
		Object.keys(quantities).forEach((licenseKey, index) => {
			const licenseTerm =
				quantities[licenseKey] +
				' ' +
				translate(() => UserLicenseLabels[licenseKey as UserLicenseModel]);
			if (!licensesString) licensesString = licenseTerm;
			else
				licensesString =
					licensesString +
					` ${
						index === Object.keys(quantities).length - 1
							? translate(({ terms }) => terms.andLowerCase)
							: ', '
					} ` +
					licenseTerm;
		});

		return licensesString;
	}

	return (
		<Wrapper>
			<SubscriptionIcon />
			<LicenseInfoContainer>
				<Typography.H3>
					{translate(({ accountUM }) =>
						isNonSubscriber
							? accountUM.subscription.noSubscriptionPlan
							: details?.subscriptionName
							? SubscriptionNameLabel[details?.subscriptionName]
							: ''
					)}
				</Typography.H3>

				{isSubscriptionUpdating && <UpdatingSubscriptionText />}

				{!isSubscriptionUpdating && (
					<>
						<Typography.Caption>
							{totalLicensesCount === 0
								? translate(({ accountUM }) => accountUM.subscription.noLicense)
								: generateTotalLicensesText()}
						</Typography.Caption>

						{totalLicensesCount > 0 && (
							<Typography.Paragraph>
								{generateActiveLicensesText()}
							</Typography.Paragraph>
						)}

						<Spacer size={s => s.s} />

						{isSubscriptionLicenseUser && (
							<Typography.Paragraph color={Colors.text.disabled}>
								{translate(
									({ accountUM }) => accountUM.subscription.licenseManagedBy
								)}
								{licenseOwnerFullName}
							</Typography.Paragraph>
						)}

						{canChangePlan && (!subscriptionBundleFlag || isNonSubscriber) && (
							<a
								href="mailto:support@ledidi.no"
								className="text-blue-600 underline underline-offset-4 hover:text-blue-700 transition-colors"
							>
								{translate(
									({ accountUM }) =>
										accountUM.subscription.contactSupportToChangePlan
								)}
							</a>
						)}

						{subscriptionBundleFlag && canCancelSubscription && (
							<Button
								title={translate(
									({ accountUM }) =>
										accountUM.cancelSubscription.cancelSusbcription
								)}
								variant={v => v.link}
								paddingOffset={{ all: 0 }}
								onClick={() => setChangePlanModalVisible(true)}
							/>
						)}

						{subscriptionBundleFlag && isLedidiFreeTrial && (
							<Button
								title={translate(
									({ accountUM }) => accountUM.cancelSubscription.cancelFreeTrial
								)}
								variant={v => v.link}
								paddingOffset={{ all: 0 }}
								onClick={() => setChangePlanModalVisible(true)}
							/>
						)}
					</>
				)}
			</LicenseInfoContainer>
			{subscriptionApiKey && (
				<Flex align={a => a.center}>
					{
						<Typography.Paragraph>
							{translate(({ accountUM }) => accountUM.subscription.apiKey, false, {
								apiKey: subscriptionApiKey
							})}
						</Typography.Paragraph>
					}
				</Flex>
			)}
		</Wrapper>
	);
}
